import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DCWMPStep5WasteEstimates = ({ project, onComplete, onBack }) => {
  const [wasteData, setWasteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadWasteCalculations();
  }, [project?.id]);

  const loadWasteCalculations = async () => {
    try {
      const token = localStorage.getItem('opwmp_token');
      const response = await axios.get(
        `${API}/api/projects/${project.id}/dcwmp/calculate-waste`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWasteData(response.data);
    } catch (err) {
      setError('Failed to load waste calculations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      // Save the calculated waste data
      const payload = {
        demolition_materials: wasteData.demolition?.materials || [],
        total_demolition_volume: wasteData.demolition?.total_volume || 0,
        demolition_diversion_rate: wasteData.demolition?.diversion_rate || 0,
        construction_materials: wasteData.construction?.materials || [],
        total_construction_volume: wasteData.construction?.total_volume || 0,
        construction_diversion_rate: wasteData.construction?.diversion_rate || 0,
        excavation_volume: wasteData.excavation?.total_volume || 0,
        excavation_onsite_reuse: wasteData.excavation?.onsite_reuse || 0,
        excavation_exported: wasteData.excavation?.exported || 0
      };

      await axios.put(
        `${API}/api/projects/${project.id}/dcwmp/step5`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow-lg rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Calculating waste estimates...</span>
        </div>
      </div>
    );
  }

  const hasDemolition = wasteData?.demolition?.has_demolition;
  const hasExcavation = wasteData?.excavation?.total_volume > 0;

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-white/30 rounded-md text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-white">Waste Estimates</h2>
        <p className="text-purple-100 mt-1">Review calculated waste volumes for demolition and construction phases</p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600 italic">
            <strong>Note:</strong> All waste quantities are indicative estimates prepared for development 
            application and planning approval purposes only. Actual waste generation will vary depending 
            on construction methodology, site conditions, contractor practices and material reuse opportunities.
          </p>
        </div>

        {/* Demolition Waste Table */}
        {hasDemolition && (
          <div>
            <h3 className="text-lg font-semibold text-amber-700 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Table 1: Demolition Waste - Expected Material Streams
            </h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead className="bg-amber-600">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Material Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Est. m³</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">On-site Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Off-site Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Disposal</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {wasteData.demolition?.materials?.map((mat, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2 text-sm text-gray-900">{mat.material_type}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 text-center font-medium">{mat.estimated_volume.toFixed(1)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{mat.onsite_action}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{mat.offsite_action}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{mat.disposal_action}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-amber-800">TOTAL</td>
                    <td className="px-4 py-3 text-sm text-amber-800 text-center">{wasteData.demolition?.total_volume?.toFixed(1)}</td>
                    <td colSpan="3" className="px-4 py-3 text-sm text-amber-700">
                      Diversion Rate: {wasteData.demolition?.diversion_rate?.toFixed(0)}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Construction Waste Table */}
        <div>
          <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Table 2: Construction Waste - Expected Material Streams
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead className="bg-green-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Material Type</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Est. m³</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">On-site Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Off-site Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Disposal</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {wasteData.construction?.materials?.map((mat, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm text-gray-900">{mat.material_type}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 text-center font-medium">{mat.estimated_volume.toFixed(1)}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{mat.onsite_action}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{mat.offsite_action}</td>
                    <td className="px-4 py-2 text-sm text-gray-600">{mat.disposal_action}</td>
                  </tr>
                ))}
                <tr className="bg-green-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-green-800">TOTAL</td>
                  <td className="px-4 py-3 text-sm text-green-800 text-center">{wasteData.construction?.total_volume?.toFixed(1)}</td>
                  <td colSpan="3" className="px-4 py-3 text-sm text-green-700">
                    Diversion Rate: {wasteData.construction?.diversion_rate?.toFixed(0)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Excavation Summary */}
        {hasExcavation && (
          <div>
            <h3 className="text-lg font-semibold text-blue-700 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              Excavation Materials
            </h3>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Total Volume</p>
                  <p className="text-2xl font-bold text-blue-700">{wasteData.excavation?.total_volume?.toLocaleString()} m³</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">On-site Reuse</p>
                  <p className="text-2xl font-bold text-green-600">{wasteData.excavation?.onsite_reuse?.toLocaleString()} m³</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Exported</p>
                  <p className="text-2xl font-bold text-amber-600">{wasteData.excavation?.exported?.toLocaleString()} m³</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-purple-800 mb-4">Project Summary</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {hasDemolition && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-600">Demolition Phase</p>
                <p className="text-2xl font-bold text-amber-700">{wasteData.demolition?.total_volume?.toLocaleString()} m³</p>
                <p className="text-sm text-green-600">{wasteData.demolition?.diversion_rate?.toFixed(0)}% diversion target</p>
              </div>
            )}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600">Construction Phase</p>
              <p className="text-2xl font-bold text-green-700">{wasteData.construction?.total_volume?.toLocaleString()} m³</p>
              <p className="text-sm text-green-600">{wasteData.construction?.diversion_rate?.toFixed(0)}% diversion target</p>
            </div>
            {hasExcavation && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-600">Excavation Materials</p>
                <p className="text-2xl font-bold text-blue-700">{wasteData.excavation?.total_volume?.toLocaleString()} m³</p>
                <p className="text-sm text-blue-600">{((wasteData.excavation?.onsite_reuse / wasteData.excavation?.total_volume) * 100).toFixed(0)}% reused on-site</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DCWMPStep5WasteEstimates;
